import { Button, Group, Select, Stack, Switch, Text } from "@mantine/core";
import { IconDeviceFloppy } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";

interface IScheduleSelector {
  expression?: string;
  onSave: (jobEnabled: boolean, jobInterval: string) => void;
  scheduleActive: boolean;
  disabled: boolean;
}

interface ICronParts {
  time: string;
  dayOfWeek: number;
  daily: boolean;
}

function ScheduleSelector({ expression, onSave, scheduleActive, disabled }: IScheduleSelector) {
  const dayList = useMemo(() => {
    return [
      { value: "1", label: "Mandag" },
      { value: "2", label: "Tirsdag" },
      { value: "3", label: "Onsdag" },
      { value: "4", label: "Torsdag" },
      { value: "5", label: "Fredag" },
      { value: "6", label: "Lørdag" },
      { value: "0", label: "Søndag" },
    ];
  }, []);

  const hourList = useMemo(() => {
    return Array.from({ length: 24 }, (_, i) => {
      const value = i.toString().padStart(2, "0");
      return { value, label: value };
    });
  }, []);

  const generateCronExpression = ({ time, dayOfWeek, daily }: ICronParts): string => {
    let _time = time;
    let _dayOfWeek = dayOfWeek;

    if (/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/.test(time) === false) {
      console.warn("Invalid time format");
      _time = "00:00";
    }

    if (_dayOfWeek < 0 || _dayOfWeek > 6) {
      console.warn("Invalid day of week");
      _dayOfWeek = 1;
    }

    const [hours, minutes] = _time.split(":");
    const dayOfWeekField = daily === true ? "*" : _dayOfWeek;

    return `${minutes} ${hours} * * ${dayOfWeekField}`;
  };

  const parseCronExpression = (cronExpression: string): ICronParts => {
    if (cronExpression == null) {
      return null;
    }

    const parts = cronExpression.split(" ");

    if (parts.length !== 5) {
      console.warn("Invalid cron expression");
      return {
        time: "00:00",
        dayOfWeek: 1,
        daily: false,
      };
    }

    const minutesPart = parts[0] !== "*" ? parts[0] : "00";
    const hoursPart = parts[1] !== "*" ? parts[1] : "00";
    const dayOfWeekPart = parts[4];

    const daily = dayOfWeekPart === "*";
    const time = `${hoursPart.padStart(2, "0")}:${minutesPart.padStart(2, "0")}`;
    const dayOfWeek = daily === true ? 1 : parseInt(dayOfWeekPart, 10);

    return {
      time,
      dayOfWeek,
      daily,
    };
  };

  const [cronParts, setCronParts] = useState<ICronParts>(null);

  useEffect(() => {
    const parts = parseCronExpression(expression);

    if (parts == null) {
      return;
    }

    setDaily(parts.daily);
    setDayOfWeek(parts.dayOfWeek);
    setTime(parts.time);
    setCronParts(parts);
  }, [expression]);

  const updateCronExpression = (parts: ICronParts) => {
    const cronExpression = generateCronExpression(parts);
    onSave(jobEnabled, cronExpression);
  };

  const saveDisabled = () => {
    return (
      scheduleActive === jobEnabled &&
      cronParts?.daily === daily &&
      cronParts?.dayOfWeek === dayOfWeek &&
      cronParts?.time === time
    );
  };

  const [jobEnabled, setJobEnabled] = useState<boolean>(null);
  const [daily, setDaily] = useState<boolean>(false);
  const [dayOfWeek, setDayOfWeek] = useState<number>(1);
  const [time, setTime] = useState<string>("00:00");

  useEffect(() => {
    setJobEnabled(scheduleActive);
  }, [scheduleActive]);

  const inputsDisabled = disabled === true || jobEnabled === false;

  return (
    <Group align="flex-end" gap="sm" wrap="wrap">
      <Stack gap={4}>
        <Text size="sm" fw={600}>
          Aktiv
        </Text>
        <Switch checked={jobEnabled === true} disabled={disabled === true} onChange={(e) => setJobEnabled(e.currentTarget.checked)} />
      </Stack>

      <Select
        label="Frekvens"
        w="10rem"
        disabled={inputsDisabled}
        value={daily === true ? "daily" : "weekly"}
        data={[
          { value: "daily", label: "Hver dag" },
          { value: "weekly", label: "Hver uke" },
        ]}
        onChange={(value) => setDaily(value === "daily")}
      />

      {daily === false && (
        <Select
          label="Dag"
          w="10rem"
          disabled={inputsDisabled}
          value={dayOfWeek.toString()}
          data={dayList}
          onChange={(value) => setDayOfWeek(Number(value ?? 1))}
        />
      )}

      <Select
        label="Time"
        w="7rem"
        disabled={inputsDisabled}
        value={time?.split(":")[0] ?? "00"}
        data={hourList}
        onChange={(value) => setTime(`${value ?? "00"}:00`)}
      />

      <Button
        color="green"
        disabled={disabled === true || saveDisabled()}
        leftSection={<IconDeviceFloppy size={16} />}
        onClick={() => updateCronExpression({ time, dayOfWeek, daily })}
      >
        Lagre
      </Button>
    </Group>
  );
}

export default ScheduleSelector;
